import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Autocomplete,
  Chip,
  Collapse,
  Divider,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Add, Delete, DeleteForever, Search, ExpandMore, ExpandLess, Refresh, PictureAsPdf, Download, FilterList, Clear, Archive, Restore, Edit } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';
import {
  inventoryOptionLabel,
  inventoryPickerOptions,
  inventorySerialOptions,
  inventoryBatchOptions,
  buildIssuanceUnitDetailsFromInventory,
  issuanceDisplayUnitDetails,
  getInventoryAircraftLabel,
  findPreferredInventoryForPart
} from '../../utils/partMasterLabel';
import { PART_CONDITION_LABELS } from '../../constants/partUnitConditions';
import { STORE_TYPES, normalizeStoreTypeValue } from '../../constants/storeTypes';
import IssuanceReviewDialog from './IssuanceReviewDialog';
import CertificateField from './CertificateField';
import {
  findInventoryCertificateDocument,
  downloadInventoryCertificate
} from '../../utils/storeCertificateDocuments';
import {
  canCorrectIssuance,
  canManageIssuanceChallan,
  canReviewIssuance,
  isIssuancePendingApproval,
  isIssuanceRejected,
  isIssuanceReviewable,
  isIssuanceSubmitter,
  isRejectedIssuanceAwaitingRevision
} from '../../utils/issuancePermissions';
import ForceDeleteIssuanceDialog from './ForceDeleteIssuanceDialog';
import {
  isQuantityDraftInput,
  parseQuantityFloat,
  unitRowCountForQuantity
} from '../../utils/quantityFloat';
import { sanitizeUnitDetailsForApiSubmit } from '../../utils/inventoryItemForm';
import { getWorkOrderDisplayNumber } from '../../utils/workOrderDisplayNumber';
import { useWorkOrderDisplayContext } from '../maintenance/hooks/useWorkOrderDisplayContext';

function formatExpiryDisplay(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

const createEmptyIssuanceFormData = () => ({
  workOrderId: '',
  aircraftId: '',
  recipientType: 'USER',
  issuedTo: '',
  issuedToExternalName: '',
  issuedToExternalCompany: '',
  issuedToExternalPhone: '',
  returnDueDate: null,
  deliveryNote: '',
  operatorId: '',
  issuedFromAddressId: '',
  counterpartyCompany: '',
  counterpartyContactName: '',
  counterpartyContactPhone: '',
  loanReferenceNumber: ''
});

function IssuanceItemDetailsPanel({ inventoryItem, line }) {
  const rows = issuanceDisplayUnitDetails(inventoryItem, line);
  if (!rows.length) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Item details (from store inventory)
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Serial</TableCell>
            <TableCell>Condition</TableCell>
            <TableCell>Batch</TableCell>
            <TableCell>Expiry</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((unit) => (
            <TableRow key={unit.serialNumber}>
              <TableCell sx={{ fontFamily: 'monospace' }}>{unit.serialNumber || '—'}</TableCell>
              <TableCell>{PART_CONDITION_LABELS[unit.condition] || unit.condition || '—'}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace' }}>{unit.batchNumber || '—'}</TableCell>
              <TableCell>{formatExpiryDisplay(unit.expiryDate)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {inventoryItem && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {inventoryItem.certificateNumber && (
            <Typography variant="caption" color="text.secondary">
              Certificate: <strong>{inventoryItem.certificateNumber}</strong>
            </Typography>
          )}
          {inventoryItem.purchaseOrderNumber && (
            <Typography variant="caption" color="text.secondary">
              PO: <strong>{inventoryItem.purchaseOrderNumber}</strong>
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

const ISSUED_TO_FILTER_EXTERNAL = '__EXTERNAL__';

function issuanceRecordApi(issuance) {
  return String(issuance?.inventoryDomain || '').toUpperCase() === 'LOAN'
    ? (path) => `/api/store/loan${path}`
    : (path) => `/api/store${path}`;
}

function getIssuanceItemDescription(item, inventoryById, inventoryByPartNumber) {
  const populated = item?.inventoryItemId?.description;
  if (populated) return populated;
  const invId = item?.inventoryItemId?._id || item?.inventoryItemId;
  const fromId = invId ? inventoryById.get(String(invId)) : null;
  if (fromId?.description) return fromId.description;
  const fromPart = inventoryByPartNumber.get(String(item?.partNumber || '').trim().toUpperCase());
  return fromPart?.description || '';
}

const IssuanceModule = ({ recordId: linkRecordId, recordType, inventoryDomain = 'STORE' }) => {
  const storeApi = useMemo(
    () => (path) => `/api/store${inventoryDomain === 'LOAN' ? '/loan' : ''}${path}`,
    [inventoryDomain]
  );
  const [inventory, setInventory] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [aircraftList, setAircraftList] = useState([]);
  const [users, setUsers] = useState([]);
  const [dispatchMode, setDispatchMode] = useState('STANDARD');
  const [formData, setFormData] = useState(createEmptyIssuanceFormData);
  const [operators, setOperators] = useState([]);
  const [issuedFromAddresses, setIssuedFromAddresses] = useState([]);
  const [addressDialog, setAddressDialog] = useState({
    open: false,
    saving: false,
    form: { label: '', name: '', address: '', location: '', isDefault: false }
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [issuances, setIssuances] = useState([]);
  const [loadingIssuances, setLoadingIssuances] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [partNumberFilter, setPartNumberFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [issuedToFilter, setIssuedToFilter] = useState('ALL');
  const [workOrderFilter, setWorkOrderFilter] = useState('ALL');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveDialog, setArchiveDialog] = useState({ open: false, issuance: null, action: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, issuance: null });
  const [forceDeleteDialog, setForceDeleteDialog] = useState({ open: false, issuance: null });
  const [reviewDialog, setReviewDialog] = useState({ open: false, issuance: null });
  const [reviewLoading, setReviewLoading] = useState(false);
  const [signedChallanUploadingId, setSignedChallanUploadingId] = useState('');
  const [challanGeneratingId, setChallanGeneratingId] = useState('');
  const [downloadingCertDocKey, setDownloadingCertDocKey] = useState('');
  const [unitDetailsDialog, setUnitDetailsDialog] = useState({ open: false, rowIndex: null, rows: [] });
  const [quantityDrafts, setQuantityDrafts] = useState({});
  const { showToast } = useToast();
  const { user } = useAuth();
  const woDisplayContext = useWorkOrderDisplayContext();

  const formatWorkOrderOptionLabel = useCallback((wo) => {
    if (!wo) return 'N/A';
    const number = getWorkOrderDisplayNumber(wo, woDisplayContext);
    const title = String(wo.title || '').trim();
    const reg = wo.aircraftId?.registration || 'N/A';
    if (number && title) return `${number} — ${title} - ${reg}`;
    if (number) return `${number} - ${reg}`;
    if (title) return `${title} - ${reg}`;
    return `N/A - ${reg}`;
  }, [woDisplayContext]);

  const getIssuanceWorkOrderLabel = useCallback((issuance) => {
    if (!issuance) return 'N/A';
    const wo = typeof issuance.workOrderId === 'object' ? issuance.workOrderId : null;
    const number = issuance.workOrderNumber
      || getWorkOrderDisplayNumber(wo, woDisplayContext)
      || '';
    const title = issuance.workOrderTitle || wo?.title || '';
    if (number && title && number !== title) return `${number} — ${title}`;
    return number || title || 'N/A';
  }, [woDisplayContext]);

  const canEdit = [ROLES.STORE_INSPECTOR, ROLES.STORE_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN].includes(user?.role);
  const canManageChallan = canManageIssuanceChallan(user?.role);
  const isStoreExecutive = user?.role === ROLES.STORE_EXECUTIVE;
  const canReview = canReviewIssuance(user?.role);
  // Store Manager / AM / Super Admin can cancel a mistaken issuance and restore stock
  const canCancelIssuanceWithStockRestore = [
    ROLES.STORE_INSPECTOR,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.SUPER_ADMIN
  ].includes(user?.role);
  const inventoryById = useMemo(
    () => new Map(inventory.map((inv) => [String(inv._id), inv])),
    [inventory]
  );
  const inventoryByPartNumber = useMemo(() => {
    const map = new Map();
    for (const inv of inventory) {
      const key = String(inv.partNumber || '').trim().toUpperCase();
      if (key && !map.has(key)) map.set(key, inv);
    }
    return map;
  }, [inventory]);

  const getIssuanceRecipientLabel = useCallback((issuance) => {
    if (issuance?.loanDirection === 'LOAN_OUT') {
      const company = String(issuance?.counterpartyCompany || '').trim();
      const contact = String(issuance?.counterpartyContactName || '').trim();
      if (contact && company) return `${contact} (${company})`;
      if (contact || company) return contact || company;
    }
    if (issuance?.issuedTo && (issuance.issuedTo.firstName != null || issuance.issuedTo.lastName != null)) {
      const n = `${issuance.issuedTo.firstName || ''} ${issuance.issuedTo.lastName || ''}`.trim();
      if (n) return n;
    }
    const ext = String(issuance?.issuedToExternalName || '').trim();
    if (ext) {
      const org = String(issuance?.issuedToExternalCompany || '').trim();
      return org ? `${ext} (${org})` : ext;
    }
    return 'N/A';
  }, []);

  const getIssuanceAircraftLabel = useCallback((issuance) => (
    issuance?.aircraftId?.registration
    || issuance?.aircraftRegistration
    || issuance?.workOrderId?.aircraftId?.registration
    || 'N/A'
  ), []);

  const getItemCertificateDocument = useCallback((item) => {
    const invId = item?.inventoryItemId?._id || item?.inventoryItemId;
    const populatedDocs = item?.inventoryItemId?.documents;
    const storeDocs = invId ? inventoryById.get(String(invId))?.documents : null;
    const partNumber = item?.inventoryItemId?.partNumber
      || inventoryById.get(String(invId))?.partNumber
      || item?.partNumber;
    return findInventoryCertificateDocument(populatedDocs || storeDocs || [], partNumber);
  }, [inventoryById]);

  const getItemCertificateNumber = useCallback((item, invFromStore) => (
    item?.inventoryItemId?.certificateNumber
    || invFromStore?.certificateNumber
    || ''
  ), []);

  const issuanceMatchesFilters = useCallback(
    (issuance) => {
      if (statusFilter !== 'ALL' && issuance.status !== statusFilter) return false;
      if (issuedToFilter !== 'ALL') {
        if (issuedToFilter === ISSUED_TO_FILTER_EXTERNAL) {
          const ext = String(issuance.issuedToExternalName || '').trim();
          if (!ext || issuance.issuedTo) return false;
        } else {
          const userId =
            typeof issuance.issuedTo === 'object'
              ? issuance.issuedTo?._id?.toString()
              : issuance.issuedTo?.toString();
          if (userId !== issuedToFilter.toString()) return false;
        }
      }
      if (workOrderFilter !== 'ALL') {
        const woId =
          typeof issuance.workOrderId === 'object'
            ? issuance.workOrderId?._id?.toString()
            : issuance.workOrderId?.toString();
        if (woId !== workOrderFilter.toString()) return false;
      }
      if (dateFromFilter) {
        const fromDate = new Date(dateFromFilter);
        fromDate.setHours(0, 0, 0, 0);
        const issuanceDate = new Date(issuance.issuedDate);
        if (issuanceDate < fromDate) return false;
      }
      if (dateToFilter) {
        const toDate = new Date(dateToFilter);
        toDate.setHours(23, 59, 59, 999);
        const issuanceDate = new Date(issuance.issuedDate);
        if (issuanceDate > toDate) return false;
      }
      const pnTerm = String(partNumberFilter || '').trim().toUpperCase();
      if (pnTerm) {
        const hasPart = (issuance.items || []).some((item) => {
          const partNumber = String(item.partNumber || '').trim().toUpperCase();
          const description = String(
            getIssuanceItemDescription(item, inventoryById, inventoryByPartNumber)
          ).trim().toUpperCase();
          return partNumber.includes(pnTerm) || description.includes(pnTerm);
        });
        if (!hasPart) return false;
      }
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const issuedToLabel = getIssuanceRecipientLabel(issuance).toLowerCase();
        const workOrderLabel = getIssuanceWorkOrderLabel(issuance).toLowerCase();
        const aircraftLabel = getIssuanceAircraftLabel(issuance).toLowerCase();
        const challanNumber = issuance.challanNumber?.toLowerCase() || '';
        const issuedByName = `${issuance.issuedBy?.firstName || ''} ${issuance.issuedBy?.lastName || ''}`.toLowerCase();
        const loanRef = String(issuance.loanReferenceNumber || '').toLowerCase();
        const counterparty = String(issuance.counterpartyCompany || '').toLowerCase();
        const approval = String(issuance.approvalStatus || '').toLowerCase();
        if (
          !issuedToLabel.includes(term) &&
          !workOrderLabel.includes(term) &&
          !aircraftLabel.includes(term) &&
          !challanNumber.includes(term) &&
          !issuedByName.includes(term) &&
          !loanRef.includes(term) &&
          !counterparty.includes(term) &&
          !approval.includes(term)
        ) {
          return false;
        }
      }
      return true;
    },
    [
      statusFilter,
      issuedToFilter,
      workOrderFilter,
      dateFromFilter,
      dateToFilter,
      partNumberFilter,
      searchTerm,
      getIssuanceRecipientLabel,
      getIssuanceWorkOrderLabel,
      getIssuanceAircraftLabel,
      inventoryById,
      inventoryByPartNumber
    ]
  );

  const filteredIssuances = useMemo(
    () => issuances.filter((issuance) => issuanceMatchesFilters(issuance)),
    [issuances, issuanceMatchesFilters]
  );

  const fetchIssuances = useCallback(async () => {
    try {
      setLoadingIssuances(true);
      const params = { includeArchived: showArchived };
      if (inventoryDomain === 'STORE') {
        params.includeLoanOut = 'true';
      }
      const response = await axios.get(storeApi('/issuances'), { params });
      setIssuances(response.data);
    } catch (error) {
      console.error('Error fetching issuances:', error);
      showToast('Error loading issuance history', 'error');
    } finally {
      setLoadingIssuances(false);
    }
  }, [showArchived, showToast, storeApi, inventoryDomain]);

  useEffect(() => {
    fetchInventory();
    fetchWorkOrders();
    fetchAircraft();
    fetchOperators();
    fetchIssuedFromAddresses();
  }, []);

  useEffect(() => {
    if (formData.recipientType !== 'USER' || dispatchMode === 'LOAN_OUT') return;
    fetchUsers();
  }, [formData.recipientType, dispatchMode]);

  useEffect(() => {
    fetchIssuances();
  }, [fetchIssuances]);

  useEffect(() => {
    if (recordType === 'issuance' && linkRecordId && issuances.length > 0) {
      const exists = issuances.some((i) => String(i._id) === String(linkRecordId));
      if (exists) setExpandedRows((prev) => ({ ...prev, [linkRecordId]: true }));
    }
  }, [recordType, linkRecordId, issuances]);

  const fetchInventory = async () => {
    try {
      const response = await axios.get(storeApi('/inventory?status=IN_INVENTORY'));
      setInventory(response.data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const fetchWorkOrders = async () => {
    try {
      const response = await axios.get('/api/maintenance');
      setWorkOrders(response.data);
    } catch (error) {
      console.error('Error fetching work orders:', error);
    }
  };

  const fetchAircraft = async () => {
    try {
      const res = await axios.get('/api/aircraft');
      const list = Array.isArray(res.data) ? res.data : res.data?.aircraft || res.data?.data || [];
      setAircraftList(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error fetching aircraft for issuance:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(storeApi('/issuance-recipients'));
      const mroUsers = response.data || [];
      setUsers(mroUsers);
      if (mroUsers.length === 0) {
        showToast('No MRO users found. Please ensure MRO users are available.', 'warning');
      }
    } catch (error) {
      console.error('Error fetching issuance recipients:', error);
      showToast('Error loading MRO users list', 'error');
    }
  };

  const fetchOperators = async () => {
    try {
      const response = await axios.get('/api/master-data/operators', { params: { isActive: 'true' } });
      setOperators(response.data || []);
    } catch (error) {
      console.error('Error fetching operators:', error);
    }
  };

  const fetchIssuedFromAddresses = async () => {
    try {
      const response = await axios.get('/api/store/issued-from-addresses', { params: { isActive: 'true' } });
      const addresses = response.data || [];
      setIssuedFromAddresses(addresses);
      const defaultAddress = addresses.find((addr) => addr.isDefault) || addresses[0];
      if (defaultAddress) {
        setFormData((prev) => (
          prev.issuedFromAddressId ? prev : { ...prev, issuedFromAddressId: defaultAddress._id }
        ));
      }
    } catch (error) {
      console.error('Error fetching issued from addresses:', error);
    }
  };

  const openAddressDialog = () => {
    const selected = issuedFromAddresses.find((addr) => String(addr._id) === String(formData.issuedFromAddressId));
    setAddressDialog({
      open: true,
      saving: false,
      form: {
        label: selected?.label || '',
        name: selected?.name || '',
        address: selected?.address || '',
        location: selected?.location || '',
        isDefault: Boolean(selected?.isDefault)
      }
    });
  };

  const handleSaveIssuedFromAddress = async () => {
    const { label, name, address, location, isDefault } = addressDialog.form;
    if (!String(label).trim() || !String(name).trim() || !String(address).trim()) {
      showToast('Label, name, and address are required', 'error');
      return;
    }
    try {
      setAddressDialog((prev) => ({ ...prev, saving: true }));
      const response = await axios.post('/api/store/issued-from-addresses', {
        label: String(label).trim(),
        name: String(name).trim(),
        address: String(address).trim(),
        location: String(location || '').trim() || undefined,
        isDefault: Boolean(isDefault)
      });
      showToast('Issued from address saved', 'success');
      setAddressDialog({ open: false, saving: false, form: { label: '', name: '', address: '', location: '', isDefault: false } });
      await fetchIssuedFromAddresses();
      if (response.data?._id) {
        setFormData((prev) => ({ ...prev, issuedFromAddressId: response.data._id }));
      }
    } catch (error) {
      console.error('Error saving issued from address:', error);
      showToast(error.response?.data?.message || 'Error saving issued from address', 'error');
      setAddressDialog((prev) => ({ ...prev, saving: false }));
    }
  };

  const mapSubmitItem = (item) => ({
    inventoryItemId: item.inventoryItemId,
    quantity: item.quantity,
    serialNumber: item.serialNumber,
    serialNumbers: Array.isArray(item.serialNumbers) ? item.serialNumbers : [],
    storeType: item.storeType || undefined,
    locationText: item.locationText || undefined,
    remarks: String(item.remarks || '').trim() || undefined,
    unitDetails: sanitizeUnitDetailsForApiSubmit(
      Array.isArray(item.unitDetails) && item.unitDetails.length > 0
        ? item.unitDetails
        : (item.batchNumber
          ? Array.from({ length: unitRowCountForQuantity(item.quantity) || 1 }, (_, idx) => ({
              condition: (Array.isArray(item.unitDetails) ? item.unitDetails[idx]?.condition : null) || 'GOOD',
              batchNumber: item.batchNumber,
              serialNumber: (Array.isArray(item.unitDetails) ? item.unitDetails[idx]?.serialNumber : null) || ''
            }))
          : [])
    )
  });

  const getChallanContextPayload = () => ({
    operatorId: formData.operatorId,
    issuedFromAddressId: formData.issuedFromAddressId
  });

  const toggleRowExpansion = (issuanceId) => {
    setExpandedRows(prev => ({
      ...prev,
      [issuanceId]: !prev[issuanceId]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ISSUED':
        return 'warning';
      case 'RETURNED':
        return 'success';
      case 'PARTIAL_RETURN':
        return 'info';
      default:
        return 'default';
    }
  };

  const getLoanApprovalChipColor = (approvalStatus) => {
    switch (approvalStatus) {
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'PENDING_AM_APPROVAL':
      case 'PENDING_STORE_APPROVAL':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Only loan-out challans wait for approval; regular store issuances can generate/download anytime.
  const isLoanChallanLocked = (issuance) =>
    issuance?.loanDirection === 'LOAN_OUT' &&
    Boolean(issuance?.approvalStatus) &&
    issuance.approvalStatus !== 'APPROVED' &&
    issuance.approvalStatus !== 'NOT_REQUIRED';

  const openReviewDialog = async (issuance) => {
    if (!issuance?._id) return;
    try {
      setReviewLoading(true);
      const api = issuanceRecordApi(issuance);
      const res = await axios.get(api(`/issuances/${issuance._id}`));
      setReviewDialog({ open: true, issuance: res.data });
    } catch (error) {
      console.error('Error loading issuance for review:', error);
      showToast(error.response?.data?.message || 'Failed to load issuance for review', 'error');
    } finally {
      setReviewLoading(false);
    }
  };

  const closeReviewDialog = () => {
    setReviewDialog({ open: false, issuance: null });
  };

  const showIssuanceReviewButton = (issuance) => (
    canReview
    && isIssuanceReviewable(issuance)
    && !isIssuanceSubmitter(issuance, user?._id)
  );

  const showIssuanceSubmitterEdit = (issuance) => (
    isStoreExecutive
    && isIssuancePendingApproval(issuance)
    && isIssuanceSubmitter(issuance, user?._id)
  );

  const showIssuanceSubmitterResubmit = (issuance) => (
    isStoreExecutive
    && isRejectedIssuanceAwaitingRevision(issuance)
    && isIssuanceSubmitter(issuance, user?._id)
  );

  const showIssuanceWorkflowActions = (issuance) => (
    showIssuanceReviewButton(issuance)
    || showIssuanceSubmitterEdit(issuance)
    || showIssuanceSubmitterResubmit(issuance)
  );

  const handleClearFilters = () => {
    setPartNumberFilter('');
    setSearchTerm('');
    setStatusFilter('ALL');
    setIssuedToFilter('ALL');
    setWorkOrderFilter('ALL');
    setDateFromFilter('');
    setDateToFilter('');
  };

  const hasActiveFilters = () => {
    return partNumberFilter !== '' ||
           searchTerm !== '' ||
           statusFilter !== 'ALL' ||
           issuedToFilter !== 'ALL' ||
           workOrderFilter !== 'ALL' ||
           dateFromFilter !== '' ||
           dateToFilter !== '';
  };

  const handleArchive = async () => {
    try {
      const api = issuanceRecordApi(archiveDialog.issuance);
      await axios.put(api(`/issuances/${archiveDialog.issuance._id}/archive`));
      showToast('Issuance record archived successfully', 'success');
      setArchiveDialog({ open: false, issuance: null, action: null });
      fetchIssuances();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error archiving issuance record';
      showToast(errorMessage, 'error');
    }
  };

  const handleRestore = async () => {
    try {
      const api = issuanceRecordApi(archiveDialog.issuance);
      await axios.put(api(`/issuances/${archiveDialog.issuance._id}/restore`));
      showToast('Issuance record restored successfully', 'success');
      setArchiveDialog({ open: false, issuance: null, action: null });
      fetchIssuances();
    } catch (error) {
      showToast(error.response?.data?.message || 'Error restoring issuance record', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      const api = issuanceRecordApi(deleteDialog.issuance);
      await axios.delete(api(`/issuances/${deleteDialog.issuance._id}`));
      showToast('Issuance record permanently deleted', 'success');
      setDeleteDialog({ open: false, issuance: null });
      fetchIssuances();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error deleting issuance record';
      showToast(errorMessage, 'error');
    }
  };

  const openChallanPdf = (pdfId) => {
    const token = localStorage.getItem('token');
    window.open(`/api/v1/pdf/${pdfId}/download?token=${token}`, '_blank');
  };

  const handleDownloadCertificate = async (invId, certDoc) => {
    if (!invId || !certDoc?._id || downloadingCertDocKey) return;
    const docKey = `${invId}:${certDoc._id}`;
    setDownloadingCertDocKey(docKey);
    try {
      await downloadInventoryCertificate(invId, certDoc, undefined, inventoryDomain);
    } catch (error) {
      showToast(error.message || error.response?.data?.message || 'Failed to download certificate', 'error');
    } finally {
      setDownloadingCertDocKey('');
    }
  };

  const handleFormItemCertificateDownload = async (inventoryItemId) => {
    if (!inventoryItemId || downloadingCertDocKey) return;
    const docKey = `form:${inventoryItemId}`;
    setDownloadingCertDocKey(docKey);
    try {
      const populated = inventoryById.get(String(inventoryItemId));
      let certDoc = findInventoryCertificateDocument(
        populated?.documents || [],
        populated?.partNumber
      );
      if (!certDoc) {
        const res = await axios.get(`/api/store-support/inventory/${inventoryItemId}/documents`);
        certDoc = findInventoryCertificateDocument(res.data || [], populated?.partNumber);
      }
      if (!certDoc?._id) {
        showToast('No certificate file found for this item', 'info');
        return;
      }
      await downloadInventoryCertificate(inventoryItemId, certDoc, undefined, inventoryDomain);
    } catch (error) {
      showToast(error.message || error.response?.data?.message || 'Failed to download certificate', 'error');
    } finally {
      setDownloadingCertDocKey('');
    }
  };

  const showChallanError = (error, fallbackMessage) => {
    console.error('Error with delivery challan:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      fallbackMessage;
    if (errorMessage.includes('Template not found') || errorMessage.includes('template')) {
      showToast(
        'Delivery challan template not registered. Please contact administrator to register the template.',
        'error'
      );
    } else {
      showToast(errorMessage, 'error');
    }
  };

  const handleGenerateChallan = async (issuance, { regenerate = false, openAfter = true } = {}) => {
    if (!canManageChallan) {
      showToast('You do not have permission to generate delivery challans', 'error');
      return;
    }
    if (isLoanChallanLocked(issuance)) {
      showToast('Challan is available after the loan-out is approved', 'info');
      return;
    }
    if (issuance?.isArchived) {
      showToast('Cannot generate challan for archived issuance', 'error');
      return;
    }
    try {
      setChallanGeneratingId(issuance._id);
      const api = issuanceRecordApi(issuance);
      showToast(
        regenerate ? 'Regenerating delivery challan...' : 'Generating delivery challan...',
        'info'
      );
      const response = await axios.post(api(`/issuances/${issuance._id}/generate-challan`));
      if (response.data?.pdfId) {
        if (openAfter) {
          openChallanPdf(response.data.pdfId);
        }
        showToast(
          regenerate ? 'Delivery challan regenerated successfully' : 'Delivery challan generated successfully',
          'success'
        );
        fetchIssuances();
      } else {
        showToast('Challan generation completed but PDF ID not returned', 'warning');
      }
    } catch (error) {
      showChallanError(
        error,
        regenerate ? 'Error regenerating challan' : 'Error generating challan'
      );
    } finally {
      setChallanGeneratingId('');
    }
  };

  const handleDownloadChallan = (issuance) => {
    if (isLoanChallanLocked(issuance)) {
      showToast('Challan is available after the loan-out is approved', 'info');
      return;
    }
    const pdfId = issuance.challanPdf?.pdfId;
    if (!pdfId) {
      handleGenerateChallan(issuance);
      return;
    }
    openChallanPdf(pdfId);
  };

  const handleRegenerateChallan = (issuance) => {
    handleGenerateChallan(issuance, { regenerate: true });
  };

  const handleUploadSignedChallan = async (issuance, file) => {
    if (!issuance?._id || !file) return;
    if (isLoanChallanLocked(issuance)) {
      showToast('Signed challan upload is available after the loan-out is approved', 'info');
      return;
    }
    try {
      setSignedChallanUploadingId(issuance._id);
      const form = new FormData();
      form.append('file', file);
      const api = issuanceRecordApi(issuance);
      await axios.post(api(`/issuances/${issuance._id}/signed-challan`), form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showToast('Signed challan uploaded successfully', 'success');
      fetchIssuances();
    } catch (error) {
      console.error('Error uploading signed challan:', error);
      showToast(error.response?.data?.message || 'Error uploading signed challan', 'error');
    } finally {
      setSignedChallanUploadingId('');
    }
  };

  const handleAddItem = () => {
    setItems([...items, {
      inventoryItemId: '',
      partNumber: '',
      quantity: 1,
      batchNumber: '',
      serialNumber: '',
      serialNumbers: [],
      storeType: '',
      locationText: '',
      remarks: '',
      unitDetails: [],
      partSearch: ''
    }]);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
    setQuantityDrafts((prev) => {
      const next = { ...prev };
      delete next[index];
      const remapped = {};
      Object.keys(next).forEach((key) => {
        const k = Number(key);
        if (k > index) remapped[k - 1] = next[key];
        else remapped[key] = next[key];
      });
      return remapped;
    });
  };

  const applyQuantityToItem = (item, qty) => {
    const next = { ...item, quantity: qty };
    const serialCap = Number.isInteger(qty) ? qty : Math.max(1, Math.trunc(qty) || 1);
    if (Array.isArray(next.serialNumbers) && next.serialNumbers.length > serialCap) {
      next.serialNumbers = next.serialNumbers.slice(0, serialCap);
    }
    const currentRows = Array.isArray(next.unitDetails) ? next.unitDetails : [];
    const rowCount = unitRowCountForQuantity(qty);
    next.unitDetails = Array.from({ length: rowCount }, (_, unitIndex) => (
      currentRows[unitIndex] || {
        condition: 'GOOD',
        batchNumber: next.batchNumber || '',
        serialNumber: ''
      }
    ));
    return next;
  };

  const getIssueQuantityDisplay = (index, item) => {
    if (quantityDrafts[index] !== undefined) return quantityDrafts[index];
    if (item?.quantity != null) return String(item.quantity);
    return '1';
  };

  const handleIssueQuantityChange = (index, rawValue) => {
    if (!isQuantityDraftInput(rawValue)) return;
    setQuantityDrafts((prev) => ({ ...prev, [index]: rawValue }));
  };

  const commitIssueQuantity = (index) => {
    const draft = quantityDrafts[index];
    if (draft === undefined) return;
    const inv = inventoryById.get(String(items[index]?.inventoryItemId));
    const maxQty = inv?.quantity != null ? Number(inv.quantity) : Infinity;
    const qty = parseQuantityFloat(draft, {
      min: 0.001,
      max: Number.isFinite(maxQty) ? maxQty : Infinity,
      fallback: 0.001
    });
    setItems((prev) => prev.map((row, i) => (i === index ? applyQuantityToItem(row, qty) : row)));
    setQuantityDrafts((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const resolveItemsForSubmit = () => items.map((row, index) => {
    const draft = quantityDrafts[index];
    if (draft === undefined) return row;
    const inv = inventoryById.get(String(row.inventoryItemId));
    const maxQty = inv?.quantity != null ? Number(inv.quantity) : Infinity;
    const qty = parseQuantityFloat(draft, {
      min: 0.001,
      max: Number.isFinite(maxQty) ? maxQty : Infinity,
      fallback: 0.001
    });
    return applyQuantityToItem(row, qty);
  });

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    // If inventoryItemId changed, update partNumber
    if (field === 'inventoryItemId') {
      const item = inventoryById.get(String(value));
      if (item) {
        newItems[index].partNumber = item.partNumber;
        newItems[index].partSearch = item.partNumber || '';
        newItems[index].serialNumbers = [];
        newItems[index].batchNumber = '';
        newItems[index].storeType = item.storeType || '';
        newItems[index].locationText = item.locationText || '';
      } else {
        newItems[index].partNumber = '';
        newItems[index].partSearch = '';
        newItems[index].serialNumbers = [];
        newItems[index].batchNumber = '';
        newItems[index].storeType = '';
        newItems[index].locationText = '';
        newItems[index].unitDetails = [];
      }
    }
    if (field === 'batchNumber') {
      const selectedBatch = String(value || '').trim();
      const qty = Math.max(0, Number(newItems[index].quantity || 0));
      const existingRows = Array.isArray(newItems[index].unitDetails) ? newItems[index].unitDetails : [];
      newItems[index].batchNumber = selectedBatch;
      const rowCount = unitRowCountForQuantity(qty);
      if (rowCount > 0) {
        newItems[index].unitDetails = Array.from({ length: rowCount }, (_, unitIndex) => ({
          ...(existingRows[unitIndex] || { condition: 'GOOD', serialNumber: '' }),
          batchNumber: selectedBatch
        }));
      }
    }
    if (field === 'serialNumbers') {
      const serials = Array.from(
        new Set((Array.isArray(value) ? value : []).map((s) => String(s || '').trim()).filter(Boolean))
      );
      const qty = Number(newItems[index].quantity) || 0;
      const maxQty = (Number.isInteger(qty) ? qty : Math.max(1, Math.trunc(qty) || 1)) || serials.length || 1;
      newItems[index].serialNumbers = serials.slice(0, maxQty);
      const inv = inventoryById.get(String(newItems[index].inventoryItemId));
      if (inv && newItems[index].serialNumbers.length > 0) {
        const details = buildIssuanceUnitDetailsFromInventory(inv, newItems[index].serialNumbers);
        newItems[index].unitDetails = details;
        const uniqueBatches = [...new Set(details.map((row) => String(row?.batchNumber || '').trim()).filter(Boolean))];
        newItems[index].batchNumber = uniqueBatches.length === 1 ? uniqueBatches[0] : '';
      } else {
        newItems[index].unitDetails = newItems[index].serialNumbers.map((serial, unitIndex) => ({
          ...(Array.isArray(newItems[index].unitDetails) ? newItems[index].unitDetails[unitIndex] : null) || {
            condition: 'GOOD',
            batchNumber: newItems[index].batchNumber || ''
          },
          serialNumber: serial
        }));
      }
    }

    setItems(newItems);
  };

  const openUnitDetailsDialog = (index) => {
    const row = resolveItemsForSubmit()[index];
    if (!row) return;
    const inv = inventoryById.get(String(row.inventoryItemId));
    const serials = Array.isArray(row.serialNumbers) ? row.serialNumbers : [];
    let existing = Array.isArray(row.unitDetails) ? row.unitDetails : [];
    if (inv && serials.length > 0) {
      existing = buildIssuanceUnitDetailsFromInventory(inv, serials);
    }
    const rowCount = unitRowCountForQuantity(row.quantity);
    const rows = Array.from({ length: rowCount }, (_, unitIndex) => (
      existing[unitIndex] || { condition: 'GOOD', batchNumber: row.batchNumber || '', serialNumber: '' }
    ));
    setUnitDetailsDialog({ open: true, rowIndex: index, rows });
  };

  const saveUnitDetailsDialog = () => {
    if (unitDetailsDialog.rowIndex == null) return;
    const newItems = [...items];
    const uniqueBatches = [...new Set(
      unitDetailsDialog.rows.map((row) => String(row?.batchNumber || '').trim()).filter(Boolean)
    )];
    newItems[unitDetailsDialog.rowIndex] = {
      ...newItems[unitDetailsDialog.rowIndex],
      batchNumber: uniqueBatches.length === 1 ? uniqueBatches[0] : '',
      unitDetails: unitDetailsDialog.rows
    };
    setItems(newItems);
    setUnitDetailsDialog({ open: false, rowIndex: null, rows: [] });
  };

  const getWorkOrderRequirements = useCallback((wo) => {
    if (!wo) return [];
    const totals = new Map();
    const addRequirement = (partNumber, quantity = 1) => {
      const pn = String(partNumber || '').trim().toUpperCase();
      if (!pn) return;
      const qty = Number(quantity) > 0 ? Number(quantity) : 1;
      totals.set(pn, (totals.get(pn) || 0) + qty);
    };

    (wo.requiredSpares || []).forEach((line) => addRequirement(line.partNumber, line.quantity));
    (wo.requiredConsumables || []).forEach((line) => addRequirement(line.partNumberOrSpecification, line.quantity));
    (wo.requiredToolsEquipment || []).forEach((line) => addRequirement(line.partNumber, line.quantity || 1));

    const procurement = wo.partsProcurementRequirements || {};
    const procPartNumbers = Array.isArray(procurement.partNumbers) ? procurement.partNumbers : [];
    const procQuantities = Array.isArray(procurement.quantities) ? procurement.quantities : [];
    procPartNumbers.forEach((pn, idx) => addRequirement(pn, procQuantities[idx] || 1));

    return Array.from(totals.entries()).map(([partNumber, quantity]) => ({ partNumber, quantity }));
  }, []);

  const handleWorkOrderChange = (workOrderId) => {
    const selected = workOrders.find((wo) => String(wo._id) === String(workOrderId));
    const requirements = getWorkOrderRequirements(selected);
    const woAircraftId = selected?.aircraftId?._id || selected?.aircraftId || '';
    const preferredAircraftId = woAircraftId
      || formData.aircraftId
      || '';
    const prefilledItems = requirements.map((req) => {
      const matched = findPreferredInventoryForPart(inventory, req.partNumber, preferredAircraftId)
        || inventoryByPartNumber.get(req.partNumber);
      return {
        inventoryItemId: matched?._id || '',
        partNumber: req.partNumber,
        quantity: req.quantity,
        batchNumber: '',
        serialNumber: '',
        serialNumbers: [],
        storeType: matched?.storeType || '',
        locationText: matched?.locationText || '',
        unitDetails: Array.from({ length: unitRowCountForQuantity(req.quantity) || 1 }, () => ({ condition: 'GOOD', batchNumber: '', serialNumber: '' })),
        partSearch: req.partNumber || ''
      };
    });

    setFormData((prev) => ({
      ...prev,
      workOrderId,
      ...(woAircraftId ? { aircraftId: String(woAircraftId) } : {})
    }));
    if (workOrderId && requirements.length > 0) {
      setQuantityDrafts({});
      setItems(prefilledItems);
      const linkedCount = prefilledItems.filter((row) => row.inventoryItemId).length;
      const unlinkedCount = prefilledItems.length - linkedCount;
      if (unlinkedCount > 0) {
        showToast(
          `Loaded ${prefilledItems.length} requirement(s): ${linkedCount} linked to inventory, ${unlinkedCount} need manual selection`,
          'warning'
        );
      } else {
        showToast(`Loaded ${prefilledItems.length} requirement item(s) from selected work order`, 'success');
      }
    }
  };

  const getIssuanceApiErrorMessage = (error, fallback) => {
    const data = error?.response?.data;
    if (data?.message) return data.message;
    const first = Array.isArray(data?.errors) ? data.errors[0] : null;
    if (first?.path && first?.msg) {
      const path = String(first.path).replace(/items\[(\d+)\]\.inventoryItemId/, (_, i) => `line ${Number(i) + 1} part`);
      return `${path}: ${first.msg}`;
    }
    return fallback;
  };

  const handleSubmit = async ({ withoutWorkOrder = false } = {}) => {
    const submitItems = resolveItemsForSubmit();
    if (submitItems.length === 0) {
      showToast('Please add at least one item', 'error');
      return;
    }

    if (!formData.operatorId) {
      showToast('Please select an operator for the delivery challan', 'error');
      return;
    }
    if (!formData.issuedFromAddressId) {
      showToast('Please select an issued from address', 'error');
      return;
    }

    const unlinkedItems = submitItems.filter((item) => !String(item.inventoryItemId || '').trim());
    if (unlinkedItems.length > 0) {
      const examples = unlinkedItems
        .map((item) => item.partNumber || item.partSearch || 'Unknown part')
        .slice(0, 3)
        .join(', ');
      showToast(
        `Select each part from store inventory (${unlinkedItems.length} line(s) not linked${examples ? `: ${examples}` : ''})`,
        'error'
      );
      return;
    }

    if (dispatchMode === 'LOAN_OUT') {
      if (!String(formData.counterpartyCompany || '').trim()) {
        showToast('Recipient company is required for loan out', 'error');
        return;
      }
      try {
        setLoading(true);
        await axios.post('/api/store/loan/issue', {
          issuedTo: user?._id,
          sourceInventoryDomain: 'STORE',
          counterpartyCompany: String(formData.counterpartyCompany || '').trim(),
          counterpartyContactName: String(formData.counterpartyContactName || '').trim() || undefined,
          counterpartyContactPhone: String(formData.counterpartyContactPhone || '').trim() || undefined,
          loanReferenceNumber: String(formData.loanReferenceNumber || '').trim() || undefined,
          deliveryNote: formData.deliveryNote || undefined,
          returnDueDate: formData.returnDueDate ? new Date(formData.returnDueDate).toISOString() : undefined,
          ...getChallanContextPayload(),
          items: submitItems.map(mapSubmitItem)
        });
        showToast(
          user?.role === ROLES.STORE_EXECUTIVE
            ? 'Loan-out submitted for Store Manager approval (visible in Loan Inventory when approved)'
            : 'Loan-out submitted for AM approval (visible in Loan Inventory when approved)',
          'success'
        );
        setFormData(createEmptyIssuanceFormData());
        setDispatchMode('STANDARD');
        setQuantityDrafts({});
        setItems([]);
        fetchIssuedFromAddresses();
        fetchIssuances();
      } catch (error) {
        console.error('Error submitting loan out:', error);
        showToast(getIssuanceApiErrorMessage(error, 'Error submitting loan out'), 'error');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (formData.recipientType === 'USER' && !formData.issuedTo) {
      showToast('Please select an MRO user as recipient', 'error');
      return;
    }
    if (formData.recipientType === 'EXTERNAL' && !String(formData.issuedToExternalName || '').trim()) {
      showToast('Please enter the non-user recipient name', 'error');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...(withoutWorkOrder || !formData.workOrderId ? {} : { workOrderId: formData.workOrderId }),
        ...(formData.aircraftId ? { aircraftId: formData.aircraftId } : {}),
        ...(formData.returnDueDate ? { returnDueDate: formData.returnDueDate } : {}),
        deliveryNote: formData.deliveryNote,
        ...getChallanContextPayload(),
        issuedTo: formData.recipientType === 'USER' ? formData.issuedTo : undefined,
        issuedToExternalName:
          formData.recipientType === 'EXTERNAL' ? String(formData.issuedToExternalName || '').trim() : undefined,
        issuedToExternalCompany:
          formData.recipientType === 'EXTERNAL' ? String(formData.issuedToExternalCompany || '').trim() || undefined : undefined,
        issuedToExternalPhone:
          formData.recipientType === 'EXTERNAL' ? String(formData.issuedToExternalPhone || '').trim() || undefined : undefined,
        items: submitItems.map(mapSubmitItem)
      };
      await axios.post(storeApi('/issue'), payload);
      showToast(
        user?.role === ROLES.STORE_EXECUTIVE
          ? 'Issuance submitted for Store Manager / AM review'
          : 'Items issued successfully',
        'success'
      );
      setFormData(createEmptyIssuanceFormData());
      setQuantityDrafts({});
      setItems([]);
      fetchInventory();
      fetchIssuedFromAddresses();
      fetchIssuances(); // Refresh issuance history
    } catch (error) {
      console.error('Error issuing items:', error);
      showToast(getIssuanceApiErrorMessage(error, 'Error issuing items'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Issuance and Dispatch
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Issue parts, materials, and tools to maintenance teams. You can link items to a work order, or issue them without a work order when needed.
        Use <strong>Loan out</strong> dispatch to record a recipient and track the line in Loan Inventory (with approvals) instead of an immediate MRO issue.
        {user?.role === ROLES.STORE_EXECUTIVE && (
          <> Store Executive submissions are sent to the Store Manager or Accountable Manager for review before stock is issued.</>
        )}
        {canReview && (
          <> Use <strong>Review</strong> in issuance history to preview pending submissions, adjust lines if needed, then approve or reject.</>
        )}
      </Alert>

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">Dispatch type</FormLabel>
              <RadioGroup
                row
                value={dispatchMode}
                onChange={(e) => setDispatchMode(e.target.value)}
              >
                <FormControlLabel value="STANDARD" control={<Radio size="small" />} label="Standard (MRO / dispatch)" />
                <FormControlLabel
                  value="LOAN_OUT"
                  control={<Radio size="small" />}
                  label="Loan out (recipient / loan inventory)"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          {dispatchMode === 'LOAN_OUT' && (
            <Grid item xs={12}>
              <Alert severity="warning">
                Loan-out removes stock from the store only after approval. Balances appear under Loan Items → Loan Inventory. Returns are received via Receiving and Inspection → Loan/Rental.
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Operator *</InputLabel>
              <Select
                value={formData.operatorId}
                label="Operator *"
                onChange={(e) => setFormData((prev) => ({ ...prev, operatorId: e.target.value }))}
              >
                {operators.map((operator) => (
                  <MenuItem key={operator._id} value={operator._id}>
                    {operator.name} ({operator.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <FormControl fullWidth required>
                <InputLabel>Issued From *</InputLabel>
                <Select
                  value={formData.issuedFromAddressId}
                  label="Issued From *"
                  onChange={(e) => setFormData((prev) => ({ ...prev, issuedFromAddressId: e.target.value }))}
                >
                  {issuedFromAddresses.map((addr) => (
                    <MenuItem key={addr._id} value={addr._id}>
                      {addr.label}{addr.isDefault ? ' (Default)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={openAddressDialog} sx={{ mt: 1, whiteSpace: 'nowrap' }}>
                Save Address
              </Button>
            </Box>
            {formData.issuedFromAddressId && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-line' }}>
                {(() => {
                  const selected = issuedFromAddresses.find((addr) => String(addr._id) === String(formData.issuedFromAddressId));
                  if (!selected) return null;
                  return [selected.name, selected.address, selected.location ? `Location: ${selected.location}` : '']
                    .filter(Boolean)
                    .join('\n');
                })()}
              </Typography>
            )}
          </Grid>
          {dispatchMode === 'LOAN_OUT' && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Recipient company *"
                  value={formData.counterpartyCompany}
                  onChange={(e) => setFormData((prev) => ({ ...prev, counterpartyCompany: e.target.value }))}
                  placeholder="Recipient organisation"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Recipient contact"
                  value={formData.counterpartyContactName}
                  onChange={(e) => setFormData((prev) => ({ ...prev, counterpartyContactName: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Recipient phone"
                  value={formData.counterpartyContactPhone}
                  onChange={(e) => setFormData((prev) => ({ ...prev, counterpartyContactPhone: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Loan reference (optional)"
                  value={formData.loanReferenceNumber}
                  onChange={(e) => setFormData((prev) => ({ ...prev, loanReferenceNumber: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  label="Return due date (optional)"
                  value={formData.returnDueDate ? String(formData.returnDueDate).slice(0, 10) : ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, returnDueDate: e.target.value || null }))}
                />
              </Grid>
            </>
          )}
          {dispatchMode === 'STANDARD' && (
            <>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Work Order (Optional)</InputLabel>
                  <Select
                    value={formData.workOrderId}
                    label="Work Order (Optional)"
                    onChange={(e) => handleWorkOrderChange(e.target.value)}
                  >
                    <MenuItem value="">None</MenuItem>
                    {workOrders.map((wo) => (
                      <MenuItem key={wo._id} value={wo._id}>
                        {formatWorkOrderOptionLabel(wo)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Aircraft (Optional)</InputLabel>
                  <Select
                    value={formData.aircraftId}
                    label="Aircraft (Optional)"
                    onChange={(e) => setFormData((prev) => ({ ...prev, aircraftId: e.target.value }))}
                  >
                    <MenuItem value="">None</MenuItem>
                    {aircraftList.map((ac) => (
                      <MenuItem key={ac._id} value={ac._id}>
                        {ac.registration}{ac.model ? ` — ${ac.model}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">Issue to *</FormLabel>
                  <RadioGroup
                    row
                    value={formData.recipientType}
                    onChange={(e) => {
                      const recipientType = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        recipientType,
                        ...(recipientType === 'USER'
                          ? { issuedToExternalName: '', issuedToExternalCompany: '', issuedToExternalPhone: '' }
                          : { issuedTo: '' })
                      }));
                    }}
                  >
                    <FormControlLabel value="USER" control={<Radio size="small" />} label="MRO user" />
                    <FormControlLabel value="EXTERNAL" control={<Radio size="small" />} label="Non-user" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              {formData.recipientType === 'USER' ? (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>MRO user *</InputLabel>
                    <Select
                      value={formData.issuedTo}
                      label="MRO user *"
                      onChange={(e) => setFormData((prev) => ({ ...prev, issuedTo: e.target.value }))}
                      required
                    >
                      {users.map((u) => (
                        <MenuItem key={u._id} value={u._id}>
                          {u.firstName} {u.lastName} ({u.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      required
                      label="Recipient name *"
                      value={formData.issuedToExternalName}
                      onChange={(e) => setFormData((prev) => ({ ...prev, issuedToExternalName: e.target.value }))}
                      placeholder="Person or team receiving the items"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Company (optional)"
                      value={formData.issuedToExternalCompany}
                      onChange={(e) => setFormData((prev) => ({ ...prev, issuedToExternalCompany: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Phone (optional)"
                      value={formData.issuedToExternalPhone}
                      onChange={(e) => setFormData((prev) => ({ ...prev, issuedToExternalPhone: e.target.value }))}
                    />
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Items to Issue</Typography>
            <Button variant="outlined" startIcon={<Add />} onClick={handleAddItem}>
              Add Item
            </Button>
          </Box>

          {items.length === 0 ? (
            <Alert severity="info">Add items to issue</Alert>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1480 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 280, width: '30%' }}>Part Number</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Stock Aircraft</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>Description</TableCell>
                    <TableCell>Available Qty</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Issue Qty</TableCell>
                    <TableCell>Store Type</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>Location</TableCell>
                    <TableCell>Certificate</TableCell>
                    <TableCell>Batch Number</TableCell>
                    <TableCell>Serial Number</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>Remarks</TableCell>
                <TableCell>Unit Details</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => {
                    const inventoryItem = inventoryById.get(String(item.inventoryItemId));
                    const batchOptions = inventoryBatchOptions(inventoryItem);
                    const pickerOptions = inventoryPickerOptions(inventory, item.partSearch || '');
                    const selectedSerials = Array.isArray(item.serialNumbers) ? item.serialNumbers : [];
                    return (
                      <React.Fragment key={index}>
                      <TableRow>
                        <TableCell sx={{ minWidth: 280, verticalAlign: 'top' }}>
                          <Autocomplete
                            size="small"
                            sx={{ minWidth: 260 }}
                            options={pickerOptions}
                            value={inventoryItem || null}
                            inputValue={item.partSearch || ''}
                            onInputChange={(_, value, reason) => {
                              if (reason === 'clear') {
                                setItems((prev) => prev.map((row, i) => (i === index ? {
                                  ...row,
                                  partSearch: '',
                                  inventoryItemId: '',
                                  partNumber: '',
                                  serialNumbers: [],
                                  batchNumber: '',
                                  storeType: '',
                                  locationText: ''
                                } : row)));
                                return;
                              }
                              // Ignore Autocomplete "reset" so inventory labels never overwrite search text.
                              if (reason !== 'input') return;
                              setItems((prev) => prev.map((row, i) => {
                                if (i !== index) return row;
                                const next = { ...row, partSearch: value };
                                const selected = inventoryById.get(String(row.inventoryItemId));
                                if (selected && value !== String(selected.partNumber || '')) {
                                  next.inventoryItemId = '';
                                  next.partNumber = '';
                                  next.serialNumbers = [];
                                  next.batchNumber = '';
                                  next.storeType = '';
                                  next.locationText = '';
                                }
                                return next;
                              }));
                            }}
                            onChange={(_, selected) => {
                              if (!selected) {
                                handleItemChange(index, 'inventoryItemId', '');
                                return;
                              }
                              const newItems = [...items];
                              newItems[index] = {
                                ...newItems[index],
                                inventoryItemId: selected._id,
                                partNumber: selected.partNumber,
                                partSearch: selected.partNumber || '',
                                serialNumbers: [],
                                batchNumber: '',
                                storeType: selected.storeType || '',
                                locationText: selected.locationText || ''
                              };
                              setItems(newItems);
                            }}
                            filterOptions={(opts) => opts}
                            getOptionLabel={(option) => String(option?.partNumber || '')}
                            isOptionEqualToValue={(a, b) => String(a?._id) === String(b?._id)}
                            ListboxProps={{ style: { minWidth: 520 } }}
                            noOptionsText={
                              String(item.partSearch || '').trim()
                                ? 'No matching parts in store'
                                : 'Type part number to search store inventory'
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Type part number to search"
                                inputProps={{
                                  ...params.inputProps,
                                  style: {
                                    ...(params.inputProps?.style || {}),
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem'
                                  }
                                }}
                              />
                            )}
                            renderOption={(props, option) => (
                              <li {...props} key={option._id}>
                                <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                                  {inventoryOptionLabel(option)}
                                </Box>
                              </li>
                            )}
                          />
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            {inventoryItem ? (getInventoryAircraftLabel(inventoryItem) || '—') : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 220, whiteSpace: 'pre-wrap', wordBreak: 'break-word', verticalAlign: 'top' }}>
                          <Typography variant="body2" component="span" sx={{ display: 'block' }}>
                            {inventoryItem?.description || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{inventoryItem?.quantity || 0}</TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={getIssueQuantityDisplay(index, item)}
                            onChange={(e) => handleIssueQuantityChange(index, e.target.value)}
                            onBlur={() => commitIssueQuantity(index)}
                            inputProps={{ min: 0.001, step: 'any', max: inventoryItem?.quantity || undefined }}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            displayEmpty
                            value={normalizeStoreTypeValue(item.storeType)}
                            onChange={(e) => handleItemChange(index, 'storeType', e.target.value)}
                            sx={{ minWidth: 130 }}
                            renderValue={(selected) => {
                              const opt = STORE_TYPES.find((t) => t.value === selected);
                              return opt ? opt.label : '\u00a0';
                            }}
                          >
                            <MenuItem value="">
                              <em>Select store</em>
                            </MenuItem>
                            {STORE_TYPES.map((t) => (
                              <MenuItem key={t.value} value={t.value}>
                                {t.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          <TextField
                            size="small"
                            fullWidth
                            value={item.locationText || ''}
                            onChange={(e) => handleItemChange(index, 'locationText', e.target.value)}
                            placeholder="Location"
                            sx={{ minWidth: 160 }}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 140, verticalAlign: 'top' }}>
                          {inventoryItem ? (
                            <CertificateField
                              certificateNumber={inventoryItem.certificateNumber}
                              document={findInventoryCertificateDocument(
                                inventoryItem.documents || [],
                                inventoryItem.partNumber
                              )}
                              onDownload={() => handleFormItemCertificateDownload(inventoryItem._id)}
                              downloading={downloadingCertDocKey === `form:${inventoryItem._id}`}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 180, verticalAlign: 'top' }}>
                          <TextField
                            size="small"
                            disabled={!inventoryItem}
                            value={item.batchNumber || ''}
                            onChange={(e) => handleItemChange(index, 'batchNumber', e.target.value)}
                            placeholder={inventoryItem ? 'Select batch' : 'Select part first'}
                            inputProps={{ list: `issuance-batch-options-${index}` }}
                            helperText={
                              inventoryItem
                                ? (batchOptions.length > 0 ? 'Choose available batch' : 'No batches on inventory row')
                                : ''
                            }
                          />
                          <datalist id={`issuance-batch-options-${index}`}>
                            {batchOptions.map((batch) => (
                              <option key={batch} value={batch} />
                            ))}
                          </datalist>
                        </TableCell>
                        <TableCell sx={{ minWidth: 200, verticalAlign: 'top' }}>
                          <Autocomplete
                            multiple
                            size="small"
                            disabled={!inventoryItem}
                            options={inventorySerialOptions(inventoryItem)}
                            value={Array.isArray(item.serialNumbers) ? item.serialNumbers : []}
                            onChange={(_, selected) => {
                              const normalized = Array.from(
                                new Set((selected || []).map((serial) => String(serial || '').trim()).filter(Boolean))
                              );
                              const qty = Number(item.quantity) || 0;
                              const serialCap = Number.isInteger(qty) ? qty : Math.max(1, Math.trunc(qty) || 1);
                              handleItemChange(index, 'serialNumbers', normalized.slice(0, serialCap || 1));
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={
                                  inventoryItem
                                    ? (inventorySerialOptions(inventoryItem).length
                                      ? 'Select serial(s) from store'
                                      : 'No serials on file for this part')
                                    : 'Select a part first'
                                }
                                helperText="Optional — choose serial numbers tracked in store inventory"
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 160, verticalAlign: 'top' }}>
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            minRows={1}
                            maxRows={3}
                            value={item.remarks || ''}
                            onChange={(e) => handleItemChange(index, 'remarks', e.target.value)}
                            placeholder="Line remarks"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openUnitDetailsDialog(index)}
                            disabled={Number(item.quantity || 0) <= 0}
                          >
                            Units ({Array.isArray(item.unitDetails) ? item.unitDetails.length : 0})
                          </Button>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleRemoveItem(index)}>
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      {selectedSerials.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={13} sx={{ py: 1.5, bgcolor: 'action.hover' }}>
                            <IssuanceItemDetailsPanel inventoryItem={inventoryItem} line={item} />
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Delivery Note */}
        <Box sx={{ mt: 3 }}>
          <TextField
            fullWidth
            label="Delivery Note (Optional)"
            placeholder="Add any special instructions, handling notes, or delivery instructions..."
            value={formData.deliveryNote}
            onChange={(e) => setFormData({ ...formData, deliveryNote: e.target.value })}
            multiline
            rows={3}
            helperText="This note will be included in the delivery challan"
          />
        </Box>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
          {dispatchMode === 'LOAN_OUT' ? (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleSubmit()}
              disabled={loading || items.length === 0 || !String(formData.counterpartyCompany || '').trim()}
            >
              {loading ? 'Submitting...' : 'Submit loan out request'}
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={() => handleSubmit({ withoutWorkOrder: true })}
                disabled={
                  loading ||
                  items.length === 0 ||
                  (formData.recipientType === 'USER' ? !formData.issuedTo : !String(formData.issuedToExternalName || '').trim())
                }
              >
                {loading ? 'Issuing...' : 'Issue without WO'}
              </Button>
              <Button
                variant="contained"
                onClick={() => handleSubmit()}
                disabled={
                  loading ||
                  items.length === 0 ||
                  (formData.recipientType === 'USER' ? !formData.issuedTo : !String(formData.issuedToExternalName || '').trim())
                }
              >
                {loading ? 'Issuing...' : 'Issue Items'}
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {/* Issuance History Section */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Issuance History
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canEdit && (
              <Button
                variant={showArchived ? 'contained' : 'outlined'}
                size="small"
                startIcon={<Archive />}
                onClick={() => setShowArchived(!showArchived)}
              >
                {showArchived ? 'Hide Archived' : 'Show Archived'}
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={fetchIssuances}
              disabled={loadingIssuances}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterList color="primary" />
              <Typography variant="subtitle1" fontWeight="bold">
                Filters
              </Typography>
              {hasActiveFilters() && (
                <Chip 
                  label={`${[
                    partNumberFilter && 'Part / Description',
                    searchTerm && 'Search',
                    statusFilter !== 'ALL' && 'Status',
                    issuedToFilter !== 'ALL' && 'Recipient',
                    workOrderFilter !== 'ALL' && 'Work Order',
                    dateFromFilter && 'Date From',
                    dateToFilter && 'Date To'
                  ].filter(Boolean).length} active`}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ExpandMore />}
                onClick={() => setFiltersExpanded(!filtersExpanded)}
              >
                {filtersExpanded ? 'Hide' : 'Show'} Advanced
              </Button>
              {hasActiveFilters() && (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  startIcon={<Clear />}
                  onClick={handleClearFilters}
                >
                  Clear All
                </Button>
              )}
            </Box>
          </Box>

          <Grid container spacing={2} alignItems="center">
            {/* Quick Filters - Always Visible */}
            <Grid item xs={12} sm={6} md={5}>
              <TextField
                fullWidth
                size="small"
                label="Part Number"
                placeholder="Filter by part number or description"
                value={partNumberFilter}
                onChange={(e) => setPartNumberFilter(e.target.value)}
                inputProps={{ style: { fontFamily: 'monospace' } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: partNumberFilter && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setPartNumberFilter('')}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                placeholder="Challan, recipient, work order, loan ref..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="ALL">All Status</MenuItem>
                  <MenuItem value="ISSUED">Issued</MenuItem>
                  <MenuItem value="RETURNED">Returned</MenuItem>
                  <MenuItem value="PARTIAL_RETURN">Partial Return</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Issued To</InputLabel>
                <Select
                  value={issuedToFilter}
                  label="Issued To"
                  onChange={(e) => setIssuedToFilter(e.target.value)}
                >
                  <MenuItem value="ALL">All recipients</MenuItem>
                  <MenuItem value={ISSUED_TO_FILTER_EXTERNAL}>Non-user only</MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u._id} value={u._id}>
                      {u.firstName} {u.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Work Order</InputLabel>
                <Select
                  value={workOrderFilter}
                  label="Work Order"
                  onChange={(e) => setWorkOrderFilter(e.target.value)}
                >
                  <MenuItem value="ALL">All Work Orders</MenuItem>
                  {Array.from(new Map(
                    issuances
                      .filter(i => i.workOrderId || i.workOrderNumber)
                      .map(i => {
                        const woId = typeof i.workOrderId === 'object' ? i.workOrderId._id : i.workOrderId;
                        const label = getIssuanceWorkOrderLabel(i);
                        return [woId || label, { id: woId || label, label }];
                      })
                  ).values()).map((wo) => (
                    <MenuItem key={wo.id} value={wo.id}>
                      {wo.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Advanced Filters - Collapsible */}
            <Collapse in={filtersExpanded} timeout="auto">
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date From"
                    type="date"
                    value={dateFromFilter}
                    onChange={(e) => setDateFromFilter(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date To"
                    type="date"
                    value={dateToFilter}
                    onChange={(e) => setDateToFilter(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
        </Paper>

        {/* Results Count */}
        {hasActiveFilters() && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredIssuances.length} of {issuances.length} issuance records
            </Typography>
          </Box>
        )}

        {/* Issuance History Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50px"></TableCell>
                <TableCell>Issuance Date</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Work Order</TableCell>
                <TableCell>Aircraft</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Return Due Date</TableCell>
                <TableCell>Issued By</TableCell>
                <TableCell>Review</TableCell>
                <TableCell>Challan</TableCell>
                {canEdit && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingIssuances ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 12 : 11} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Loading issuance history...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredIssuances.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 12 : 11} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No issuance records found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredIssuances.map((issuance) => (
                    <React.Fragment key={issuance._id}>
                      <TableRow>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleRowExpansion(issuance._id)}
                          >
                            {expandedRows[issuance._id] ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          {new Date(issuance.issuedDate).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {issuance.loanDirection === 'LOAN_OUT' && (
                              <Chip size="small" label="Loan out" color="secondary" variant="outlined" />
                            )}
                            {getIssuanceRecipientLabel(issuance)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {getIssuanceWorkOrderLabel(issuance)}
                        </TableCell>
                        <TableCell>
                          {getIssuanceAircraftLabel(issuance)}
                        </TableCell>
                        <TableCell>
                          {issuance.items.length} item(s)
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            {issuance.approvalStatus &&
                              issuance.approvalStatus !== 'NOT_REQUIRED' &&
                              (isIssuancePendingApproval(issuance) ||
                                issuance.approvalStatus === 'REJECTED' ||
                                (issuance.loanDirection === 'LOAN_OUT' && issuance.approvalStatus !== 'APPROVED')) && (
                              <Chip
                                label={issuance.approvalStatus}
                                color={getLoanApprovalChipColor(issuance.approvalStatus)}
                                size="small"
                              />
                            )}
                            <Chip
                              label={issuance.status}
                              color={getStatusColor(issuance.status)}
                              size="small"
                            />
                            {issuance.isArchived && (
                              <Chip
                                label="Archived"
                                size="small"
                                color="default"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {issuance.returnDueDate 
                            ? new Date(issuance.returnDueDate).toLocaleDateString()
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {issuance.issuedBy 
                            ? `${issuance.issuedBy.firstName} ${issuance.issuedBy.lastName}`
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {showIssuanceSubmitterEdit(issuance) && (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={reviewLoading}
                                onClick={() => openReviewDialog(issuance)}
                              >
                                Edit
                              </Button>
                            )}
                            {showIssuanceSubmitterResubmit(issuance) && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                disabled={reviewLoading}
                                onClick={() => openReviewDialog(issuance)}
                              >
                                Revise & resubmit
                              </Button>
                            )}
                            {showIssuanceReviewButton(issuance) && (
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                disabled={reviewLoading}
                                onClick={() => openReviewDialog(issuance)}
                              >
                                Review
                              </Button>
                            )}
                            {!showIssuanceWorkflowActions(issuance) && (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {canManageChallan ? (
                            <>
                              {issuance.challanPdf?.pdfId ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                                  <Chip
                                    icon={<PictureAsPdf />}
                                    label={issuance.challanNumber || 'Challan'}
                                    color="success"
                                    size="small"
                                    onClick={() => handleDownloadChallan(issuance)}
                                    sx={{ cursor: 'pointer' }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    Issuance #{String(issuance._id).slice(-8).toUpperCase()}
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="text"
                                    startIcon={<Refresh />}
                                    disabled={
                                      challanGeneratingId === issuance._id ||
                                      issuance.isArchived ||
                                      isLoanChallanLocked(issuance)
                                    }
                                    onClick={() => handleRegenerateChallan(issuance)}
                                  >
                                    {challanGeneratingId === issuance._id ? 'Regenerating...' : 'Regenerate'}
                                  </Button>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Issuance #{String(issuance._id).slice(-8).toUpperCase()}
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<PictureAsPdf />}
                                    disabled={
                                      challanGeneratingId === issuance._id ||
                                      issuance.isArchived ||
                                      isLoanChallanLocked(issuance)
                                    }
                                    onClick={() => handleGenerateChallan(issuance)}
                                  >
                                    {challanGeneratingId === issuance._id ? 'Generating...' : 'Generate'}
                                  </Button>
                                </Box>
                              )}
                              {canEdit && (
                                <Box sx={{ mt: 1 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    component="label"
                                    disabled={
                                      signedChallanUploadingId === issuance._id ||
                                      isLoanChallanLocked(issuance)
                                    }
                                  >
                                    {signedChallanUploadingId === issuance._id ? 'Uploading...' : 'Upload Signed'}
                                    <input
                                      type="file"
                                      hidden
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleUploadSignedChallan(issuance, file);
                                        e.target.value = '';
                                      }}
                                    />
                                  </Button>
                                </Box>
                              )}
                            </>
                          ) : issuance.challanPdf?.pdfId ? (
                            <Chip
                              icon={<PictureAsPdf />}
                              label={issuance.challanNumber || 'Challan'}
                              color="success"
                              size="small"
                              onClick={() => handleDownloadChallan(issuance)}
                              sx={{ cursor: 'pointer' }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        {canEdit && (
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {canCorrectIssuance(user?.role, issuance, user?._id)
                                && !showIssuanceWorkflowActions(issuance) && (
                                <Tooltip title={
                                  isIssuanceRejected(issuance)
                                    ? 'Revise rejected issuance'
                                    : (isIssuanceReviewable(issuance) ? 'Review issuance' : 'Correct issuance')
                                }>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => openReviewDialog(issuance)}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {!issuance.isArchived && (
                                <Tooltip title="Archive Issuance">
                                  <IconButton 
                                    size="small" 
                                    color="warning"
                                    onClick={() => setArchiveDialog({ open: true, issuance, action: 'archive' })}
                                  >
                                    <Archive fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {issuance.isArchived && (
                                <>
                                  <Tooltip title="Restore Issuance">
                                    <IconButton 
                                      size="small" 
                                      color="primary"
                                      onClick={() => setArchiveDialog({ open: true, issuance, action: 'restore' })}
                                    >
                                      <Restore fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Permanently Delete">
                                    <IconButton 
                                      size="small" 
                                      color="error"
                                      onClick={() => setDeleteDialog({ open: true, issuance })}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                              {canCancelIssuanceWithStockRestore && (
                                <Tooltip title="Cancel issuance & restore stock — reverses inventory (qty + IN_INVENTORY) and permanently deletes this record">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setForceDeleteDialog({ open: true, issuance })}
                                  >
                                    <DeleteForever fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={canEdit ? 12 : 11} sx={{ py: 0 }}>
                          <Collapse in={expandedRows[issuance._id]} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Issued Items Details:
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ minWidth: 200, whiteSpace: 'nowrap' }}>Part Number</TableCell>
                                    <TableCell sx={{ minWidth: 100, whiteSpace: 'nowrap' }}>Stock Aircraft</TableCell>
                                    <TableCell sx={{ minWidth: 220 }}>Description</TableCell>
                                    <TableCell>Quantity</TableCell>
                                    <TableCell>Store Type</TableCell>
                                    <TableCell>Location</TableCell>
                                    <TableCell>Certificate</TableCell>
                                    <TableCell>Serial Number</TableCell>
                                    <TableCell>Unit Details</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {issuance.items.map((item, idx) => {
                                    const invId = item?.inventoryItemId?._id || item?.inventoryItemId;
                                    const invFromStore = invId ? inventoryById.get(String(invId)) : null;
                                    const populatedInv = item?.inventoryItemId && typeof item.inventoryItemId === 'object'
                                      ? item.inventoryItemId
                                      : null;
                                    const stockAircraftSource = invFromStore || populatedInv;
                                    const serials = Array.isArray(item.serialNumbers) && item.serialNumbers.length > 0
                                      ? item.serialNumbers
                                      : (item.serialNumber ? [item.serialNumber] : []);
                                    const detailRows = serials.length > 0
                                      ? issuanceDisplayUnitDetails(invFromStore, item)
                                      : [];
                                    const certDoc = getItemCertificateDocument(item);
                                    return (
                                    <React.Fragment key={idx}>
                                    <TableRow>
                                      <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                        {item.partNumber}
                                      </TableCell>
                                      <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', fontWeight: 600 }}>
                                        {stockAircraftSource
                                          ? (getInventoryAircraftLabel(stockAircraftSource) || '—')
                                          : '—'}
                                      </TableCell>
                                      <TableCell sx={{ minWidth: 220, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {getIssuanceItemDescription(item, inventoryById, inventoryByPartNumber) || '—'}
                                      </TableCell>
                                      <TableCell>{item.quantity}</TableCell>
                                      <TableCell>{item.storeType || '—'}</TableCell>
                                      <TableCell>{item.locationText || '—'}</TableCell>
                                      <TableCell>
                                        <CertificateField
                                          certificateNumber={getItemCertificateNumber(item, invFromStore)}
                                          document={certDoc}
                                          onDownload={
                                            certDoc && invId
                                              ? () => handleDownloadCertificate(invId, certDoc)
                                              : null
                                          }
                                          downloading={downloadingCertDocKey === `${invId}:${certDoc?._id}`}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        {serials.length > 0 ? serials.join(', ') : 'N/A'}
                                      </TableCell>
                                      <TableCell>
                                        {detailRows.length === 0 ? (
                                          Array.isArray(item.unitDetails) && item.unitDetails.length > 0
                                            ? item.unitDetails.map((u) => `${PART_CONDITION_LABELS[u.condition] || u.condition || 'GOOD'}${u.batchNumber ? ` / ${u.batchNumber}` : ''}${u.serialNumber ? ` / ${u.serialNumber}` : ''}`).join(', ')
                                            : 'N/A'
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">See details below</Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                    {detailRows.length > 0 && (
                                      <TableRow>
                                        <TableCell colSpan={9} sx={{ py: 1, bgcolor: 'background.default' }}>
                                          <IssuanceItemDetailsPanel inventoryItem={invFromStore} line={item} />
                                        </TableCell>
                                      </TableRow>
                                    )}
                                    </React.Fragment>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                              {issuance.deliveryNote && (
                                <>
                                  <Divider sx={{ my: 2 }} />
                                  <Typography variant="subtitle2" gutterBottom>
                                    Delivery Note:
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                                    {issuance.deliveryNote}
                                  </Typography>
                                </>
                              )}
                              <Divider sx={{ my: 2 }} />
                              {issuance.signedChallanDocument?.fileUrl && (
                                <>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Signed Challan:
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<Download />}
                                      onClick={() => window.open(issuance.signedChallanDocument.fileUrl, '_blank')}
                                    >
                                      Download Signed Challan
                                    </Button>
                                    <Typography variant="caption" color="text.secondary">
                                      {issuance.signedChallanDocument.originalName || issuance.signedChallanDocument.fileName}
                                    </Typography>
                                  </Box>
                                  <Divider sx={{ my: 2 }} />
                                </>
                              )}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Issuance Number:</strong>{' '}
                                    {String(issuance._id).slice(-8).toUpperCase()}
                                  </Typography>
                                  {issuance.challanNumber && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                      <strong>Challan Number:</strong> {issuance.challanNumber}
                                    </Typography>
                                  )}
                                  {issuance.returnDate && (
                                    <>
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        <strong>Return Date:</strong> {new Date(issuance.returnDate).toLocaleString()}
                                      </Typography>
                                      {issuance.returnReason && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                          <strong>Return Reason:</strong> {issuance.returnReason}
                                        </Typography>
                                      )}
                                    </>
                                  )}
                                </Box>
                                {canManageChallan && (
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {issuance.challanPdf?.pdfId ? (
                                      <>
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          startIcon={<Download />}
                                          disabled={isLoanChallanLocked(issuance)}
                                          onClick={() => handleDownloadChallan(issuance)}
                                        >
                                          Download Challan
                                        </Button>
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          startIcon={<Refresh />}
                                          disabled={
                                            challanGeneratingId === issuance._id ||
                                            issuance.isArchived ||
                                            isLoanChallanLocked(issuance)
                                          }
                                          onClick={() => handleRegenerateChallan(issuance)}
                                        >
                                          {challanGeneratingId === issuance._id
                                            ? 'Regenerating...'
                                            : 'Regenerate Challan'}
                                        </Button>
                                      </>
                                    ) : (
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<PictureAsPdf />}
                                        disabled={
                                          challanGeneratingId === issuance._id ||
                                          issuance.isArchived ||
                                          isLoanChallanLocked(issuance)
                                        }
                                        onClick={() => handleGenerateChallan(issuance)}
                                      >
                                        {challanGeneratingId === issuance._id
                                          ? 'Generating...'
                                          : 'Generate Challan'}
                                      </Button>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog
        open={unitDetailsDialog.open}
        onClose={() => setUnitDetailsDialog({ open: false, rowIndex: null, rows: [] })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Per Unit Condition / Batch / Serial</DialogTitle>
        <DialogContent>
        {(() => {
          const rowIndex = unitDetailsDialog.rowIndex;
          const inventoryItem = rowIndex == null ? null : inventoryById.get(String(items[rowIndex]?.inventoryItemId));
          const batchOptions = inventoryBatchOptions(inventoryItem);
          return (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Unit #</TableCell>
                <TableCell>Condition</TableCell>
                <TableCell>Batch Number</TableCell>
                <TableCell>Serial Number</TableCell>
                <TableCell>Expiry</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {unitDetailsDialog.rows.map((row, idx) => (
                <TableRow key={`issuance-unit-${idx}`}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={row.condition || 'GOOD'}
                      onChange={(e) => setUnitDetailsDialog((prev) => ({
                        ...prev,
                        rows: prev.rows.map((x, i) => (i === idx ? { ...x, condition: e.target.value } : x))
                      }))}
                    >
                      <MenuItem value="GOOD">GOOD</MenuItem>
                      <MenuItem value="DAMAGED">DAMAGED</MenuItem>
                      <MenuItem value="DEFECTIVE">DEFECTIVE</MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <TextField
                        size="small"
                        value={row.batchNumber || ''}
                        onChange={(e) => setUnitDetailsDialog((prev) => ({
                          ...prev,
                          rows: prev.rows.map((x, i) => (i === idx ? { ...x, batchNumber: e.target.value } : x))
                        }))}
                        inputProps={{ list: `issuance-unit-batches-${idx}` }}
                        placeholder={batchOptions.length > 0 ? 'Select batch' : 'Enter batch'}
                      />
                      <datalist id={`issuance-unit-batches-${idx}`}>
                        {batchOptions.map((batch) => (
                          <option key={batch} value={batch} />
                        ))}
                      </datalist>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={row.serialNumber || ''}
                      onChange={(e) => setUnitDetailsDialog((prev) => ({
                        ...prev,
                        rows: prev.rows.map((x, i) => (i === idx ? { ...x, serialNumber: e.target.value } : x))
                      }))}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatExpiryDisplay(row.expiryDate)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          );
        })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnitDetailsDialog({ open: false, rowIndex: null, rows: [] })}>Cancel</Button>
          <Button variant="contained" onClick={saveUnitDetailsDialog}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Archive/Restore Confirmation Dialog */}
      <IssuanceReviewDialog
        open={reviewDialog.open}
        issuance={reviewDialog.issuance}
        inventory={inventory}
        workOrders={workOrders}
        formatWorkOrderOptionLabel={formatWorkOrderOptionLabel}
        aircraftList={aircraftList}
        users={users}
        userRole={user?.role}
        currentUser={user}
        onClose={closeReviewDialog}
        onSaved={() => {
          fetchIssuances();
          fetchInventory();
        }}
      />

      <Dialog open={addressDialog.open} onClose={() => setAddressDialog((prev) => ({ ...prev, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>Save Issued From Address</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Label"
                value={addressDialog.form.label}
                onChange={(e) => setAddressDialog((prev) => ({ ...prev, form: { ...prev.form, label: e.target.value } }))}
                placeholder="e.g. Main Store - Mumbai"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Name"
                value={addressDialog.form.name}
                onChange={(e) => setAddressDialog((prev) => ({ ...prev, form: { ...prev.form, name: e.target.value } }))}
                placeholder="Store / warehouse name"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                minRows={3}
                label="Address"
                value={addressDialog.form.address}
                onChange={(e) => setAddressDialog((prev) => ({ ...prev, form: { ...prev.form, address: e.target.value } }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location (optional)"
                value={addressDialog.form.location}
                onChange={(e) => setAddressDialog((prev) => ({ ...prev, form: { ...prev.form, location: e.target.value } }))}
                placeholder="Warehouse bay, hangar, etc."
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={Boolean(addressDialog.form.isDefault)}
                    onChange={(e) => setAddressDialog((prev) => ({ ...prev, form: { ...prev.form, isDefault: e.target.checked } }))}
                  />
                )}
                label="Set as default issued from address"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddressDialog((prev) => ({ ...prev, open: false }))}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveIssuedFromAddress} disabled={addressDialog.saving}>
            {addressDialog.saving ? 'Saving...' : 'Save Address'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={archiveDialog.open} onClose={() => setArchiveDialog({ open: false, issuance: null, action: null })}>
        <DialogTitle>
          {archiveDialog.action === 'archive' ? 'Archive Issuance Record' : 'Restore Issuance Record'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {archiveDialog.action === 'archive'
              ? `Are you sure you want to archive this issuance record? The record will be hidden from the main list but can be restored later. Note: Only records with status "RETURNED" can be archived.`
              : `Are you sure you want to restore this issuance record? The record will be available in the issuance history again.`}
          </Typography>
          {archiveDialog.issuance && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Issuance Date:</strong> {new Date(archiveDialog.issuance.issuedDate).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Issued To:</strong> {getIssuanceRecipientLabel(archiveDialog.issuance)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Status:</strong> {archiveDialog.issuance.status}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialog({ open: false, issuance: null, action: null })}>Cancel</Button>
          <Button 
            onClick={archiveDialog.action === 'archive' ? handleArchive : handleRestore} 
            variant="contained"
            color={archiveDialog.action === 'archive' ? 'warning' : 'primary'}
          >
            {archiveDialog.action === 'archive' ? 'Archive' : 'Restore'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, issuance: null })}>
        <DialogTitle>Permanently Delete Issuance Record</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The issuance record will be permanently deleted from the system.
          </Alert>
          <Typography>
            Are you absolutely sure you want to permanently delete this issuance record?
          </Typography>
          {deleteDialog.issuance && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Issuance Date:</strong> {new Date(deleteDialog.issuance.issuedDate).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Issued To:</strong> {getIssuanceRecipientLabel(deleteDialog.issuance)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Status:</strong> {deleteDialog.issuance.status}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Items:</strong> {deleteDialog.issuance.items.length} item(s)
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, issuance: null })}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      <ForceDeleteIssuanceDialog
        open={forceDeleteDialog.open}
        issuance={forceDeleteDialog.issuance}
        onClose={() => setForceDeleteDialog({ open: false, issuance: null })}
        onDeleted={fetchIssuances}
      />
    </Box>
  );
};

export default IssuanceModule;

